import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/styles/tailwind-utils';

import type { DayOfWeek, DaySchedule } from '../types';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function parse24h(value: string): { h: number; m: number; period: 'AM' | 'PM' } {
  const [hStr = '09', mStr = '00'] = value.split(':');
  const h24 = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  const period: 'AM' | 'PM' = h24 < 12 ? 'AM' : 'PM';
  const h = h24 % 12 === 0 ? 12 : h24 % 12;
  return { h, m, period };
}

function to24h(h: number, m: number, period: 'AM' | 'PM'): string {
  let h24: number;
  if (period === 'AM') {
    h24 = h === 12 ? 0 : h;
  } else {
    h24 = h === 12 ? 12 : h + 12;
  }
  return `${String(h24).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

// ─── Icons ────────────────────────────────────────────────────────────────────

function ChevronUp() {
  return (
    <svg viewBox="0 0 16 16" fill="none" className="h-3 w-3" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 10l4-4 4 4" />
    </svg>
  );
}

function ChevronDown() {
  return (
    <svg viewBox="0 0 16 16" fill="none" className="h-3 w-3" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 6l4 4 4-4" />
    </svg>
  );
}

// ─── TimePicker ───────────────────────────────────────────────────────────────

interface TimePickerProps {
  value: string; // "HH:mm" 24-hour
  onChange: (v: string) => void;
  ariaLabel: string;
}

const spinnerBtn = 'flex h-6 w-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary';
const colLabel = 'text-[10px] font-semibold uppercase tracking-wider text-muted-foreground select-none';
const displayBox = 'flex h-10 w-12 items-center justify-center rounded-md border border-border bg-background text-lg font-semibold text-foreground select-none';

function TimePicker({ value, onChange, ariaLabel }: TimePickerProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { h, m, period } = parse24h(value);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  function update(newH: number, newM: number, newPeriod: 'AM' | 'PM') {
    onChange(to24h(newH, newM, newPeriod));
  }

  function stepHour(dir: 1 | -1) {
    const next = ((h - 1 + dir + 12) % 12) + 1;
    update(next, m, period);
  }

  function stepMinute(dir: 1 | -1) {
    const next = (m + dir * 5 + 60) % 60;
    update(h, next, period);
  }

  return (
    <div ref={ref} className="relative">
      {/* Trigger button */}
      <button
        type="button"
        aria-label={`${ariaLabel}: ${pad(h)}:${pad(m)} ${period}`}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex h-9 min-w-[112px] items-center justify-center rounded-md border px-3 text-sm font-medium transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1',
          open
            ? 'border-primary bg-brand-subtle text-foreground'
            : 'border-border bg-background text-foreground hover:border-primary/50 hover:bg-brand-subtle/50',
        )}
      >
        {pad(h)}:{pad(m)} {period}
      </button>

      {/* Picker popover */}
      {open && (
        <div
          className="absolute left-0 top-full z-50 mt-1.5 rounded-md border border-border bg-surface p-4 shadow-md"
          role="dialog"
          aria-label={`Set time: ${ariaLabel}`}
        >
          <div className="flex items-start gap-2">

            {/* ── Hour spinner ── */}
            <div className="flex flex-col items-center gap-1.5">
              <span className={colLabel}>{t('onboarding.step4.timePicker.hour')}</span>
              <button type="button" onClick={() => stepHour(1)} aria-label={t('onboarding.step4.timePicker.increaseHour')} className={spinnerBtn}>
                <ChevronUp />
              </button>
              <div className={displayBox}>{pad(h)}</div>
              <button type="button" onClick={() => stepHour(-1)} aria-label={t('onboarding.step4.timePicker.decreaseHour')} className={spinnerBtn}>
                <ChevronDown />
              </button>
            </div>

            {/* ── Colon — phantom spacers keep it aligned with the display boxes ── */}
            <div className="flex flex-col items-center gap-1.5" aria-hidden="true">
              <span className="invisible text-[10px]">H</span>
              <div className="invisible h-6" />
              <div className="flex h-10 items-center text-xl font-bold text-muted-foreground">:</div>
            </div>

            {/* ── Minute spinner ── */}
            <div className="flex flex-col items-center gap-1.5">
              <span className={colLabel}>{t('onboarding.step4.timePicker.minute')}</span>
              <button type="button" onClick={() => stepMinute(1)} aria-label={t('onboarding.step4.timePicker.increaseMinute')} className={spinnerBtn}>
                <ChevronUp />
              </button>
              <div className={displayBox}>{pad(m)}</div>
              <button type="button" onClick={() => stepMinute(-1)} aria-label={t('onboarding.step4.timePicker.decreaseMinute')} className={spinnerBtn}>
                <ChevronDown />
              </button>
            </div>

            {/* ── Vertical rule ── */}
            <div className="mx-1 self-stretch border-l border-border" aria-hidden="true" />

            {/* ── AM / PM toggle ── */}
            <div className="flex flex-col items-center gap-1.5">
              <span className={colLabel}>{t('onboarding.step4.timePicker.period')}</span>
              <div className="flex flex-col gap-1.5">
                {(['AM', 'PM'] as const).map((p) => (
                  <button
                    key={p}
                    type="button"
                    aria-pressed={period === p}
                    onClick={() => update(h, m, p)}
                    className={cn(
                      'flex h-10 w-12 items-center justify-center rounded-md text-sm font-semibold transition-colors',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                      period === p
                        ? 'bg-primary text-primary-foreground'
                        : 'border border-border bg-background text-foreground hover:bg-muted',
                    )}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}

// ─── ScheduleRow ──────────────────────────────────────────────────────────────

interface ScheduleRowProps {
  day: DayOfWeek;
  schedule: DaySchedule;
  onChange: (day: DayOfWeek, updated: DaySchedule) => void;
}

export function ScheduleRow({ day, schedule, onChange }: ScheduleRowProps) {
  const { t } = useTranslation();
  const isInvalid = schedule.open && schedule.from >= schedule.to;

  return (
    <div>
    <div className={cn(
      'flex items-center gap-4 rounded-md border bg-surface px-4 py-3',
      isInvalid ? 'border-error' : 'border-border',
    )}>
      <span className="w-28 shrink-0 text-sm font-medium text-foreground">
        {t(`onboarding.step4.days.${day}`)}
      </span>

      {/* Open/closed toggle */}
      <button
        type="button"
        role="switch"
        aria-checked={schedule.open}
        aria-label={t(`onboarding.step4.days.${day}`)}
        onClick={() => onChange(day, { ...schedule, open: !schedule.open })}
        className={cn(
          'relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent',
          'transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2',
          schedule.open ? 'bg-brand' : 'bg-border',
        )}
      >
        <span
          className={cn(
            'pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200',
            schedule.open ? 'translate-x-4' : 'translate-x-0',
          )}
        />
      </button>

      {/* Both branches wrapped in h-9 so the row height never changes */}
      {schedule.open ? (
        <div className="flex flex-1 items-center justify-center gap-2">
          <TimePicker
            value={schedule.from}
            onChange={(v) => onChange(day, { ...schedule, from: v })}
            ariaLabel={`${t(`onboarding.step4.days.${day}`)} open`}
          />
          <span className="text-sm text-muted-foreground" aria-hidden="true">
            {t('onboarding.step4.to')}
          </span>
          <TimePicker
            value={schedule.to}
            onChange={(v) => onChange(day, { ...schedule, to: v })}
            ariaLabel={`${t(`onboarding.step4.days.${day}`)} close`}
          />
        </div>
      ) : (
        <div className="flex h-9 items-center">
          <span className="text-sm text-muted-foreground">{t('onboarding.step4.closed')}</span>
        </div>
      )}
    </div>
    {isInvalid && (
      <p role="alert" className="mt-1 px-1 text-xs text-error">
        {t('onboarding.step4.invalidTimeRange')}
      </p>
    )}
    </div>
  );
}
